import React, { useState, ChangeEvent } from 'react';
import { View, Pressable, TextInput } from 'react-native-web';
import Modal from './BaseModal';
import Button from '../common/Button';
import CustomText from '../atoms/CustomText';
import { colors, spacing, typography } from '../../utils/styling';

interface ConnectProfessionalModalProps {
  isVisible: boolean;
  onClose: () => void;
}

interface FormData {
  name: string;
  phone: string;
  email: string;
  preferredContact: 'email' | 'phone';
  annualSalary: string;
  totalAssets: string;
  monthlyExpenses: string;
  confidenceLevel: number;
  thoughts: string;
  wantsContact: boolean;
}

const steps = [
  'totalAssets',
  'annualSalary',
  'monthlyExpenses',
  'confidence',
  'contact',
  'thoughts',
  'confirmation',
] as const;
type Step = typeof steps[number];

// Add currency formatting helper
function formatCurrency(value: string): string {
  if (!value) return '';
  const numeric = value.replace(/[^\d]/g, '');
  if (!numeric) return '';
  return '$' + Number(numeric).toLocaleString();
}

// Add phone formatting helper
function formatPhoneNumber(value: string): string {
  const numeric = value.replace(/[^\d]/g, '');
  if (numeric.length <= 3) return numeric;
  if (numeric.length <= 6) return `${numeric.slice(0, 3)}-${numeric.slice(3)}`;
  return `${numeric.slice(0, 3)}-${numeric.slice(3, 6)}-${numeric.slice(6, 10)}`;
}

export default function ConnectProfessionalModal({ isVisible, onClose }: ConnectProfessionalModalProps) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    phone: '',
    email: '',
    preferredContact: 'email',
    annualSalary: '',
    totalAssets: '',
    monthlyExpenses: '',
    confidenceLevel: 50,
    thoughts: '',
    wantsContact: true
  });
  const [stepIndex, setStepIndex] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  const handleChange = (field: keyof FormData, value: string | number | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNext = () => {
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  };
  const handleBack = () => {
    setStepIndex((i) => Math.max(i - 1, 0));
  };

  // Reset modal state on close or after submit
  const resetModal = () => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      preferredContact: 'email',
      annualSalary: '',
      totalAssets: '',
      monthlyExpenses: '',
      confidenceLevel: 50,
      thoughts: '',
      wantsContact: true
    });
    setStepIndex(0);
    setIsSubmitted(false);
    setShowThankYou(false);
    onClose();
  };

  const handleSubmit = async () => {
    // TODO: Implement API call to submit form data
    setIsSubmitted(true);
    setShowThankYou(true);
    setFormData({
      name: '',
      phone: '',
      email: '',
      preferredContact: 'email',
      annualSalary: '',
      totalAssets: '',
      monthlyExpenses: '',
      confidenceLevel: 50,
      thoughts: '',
      wantsContact: true
    });
    setStepIndex(0);
  };

  if (showThankYou) {
    return (
      <Modal
        isVisible={isVisible}
        onClose={resetModal}
        width={400}
        hideHeader={false}
      >
        <View style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          minHeight: 220,
          padding: spacing.lg,
        }}>
          <CustomText style={{
            fontSize: 18,
            color: colors.gray[700],
            textAlign: 'center',
            marginBottom: spacing.xl,
            fontFamily: typography.fontFamily.body,
          }}>
            Thank you for your interest! A financial professional will be in touch with you soon.
          </CustomText>
          <Button
            label="Close"
            onPress={resetModal}
            style={{ height: 48, width: '100%', backgroundColor: colors.primary, borderColor: colors.primary }}
            textStyle={{ color: colors.white }}
          />
        </View>
      </Modal>
    );
  }

  const currentStep = steps[stepIndex];
  const progress = ((stepIndex + 1) / steps.length) * 100;

  return (
    <Modal
      isVisible={isVisible}
      onClose={onClose}
      title="Connect with a Professional"
      width={800}
      hideHeader={false}
    >
      <View style={{ width: 800, minHeight: 500, maxHeight: 800, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', height: '100%' }}>
        {/* Progress Bar */}
        <View style={{ width: '100%', marginBottom: spacing.xl, marginTop: 8 }}>
          <div style={{ width: '100%', height: 8, background: colors.gray[200], borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ width: `${progress}%`, height: '100%', background: colors.primary, borderRadius: 4, transition: 'width 0.3s' }} />
          </div>
        </View>
        {/* Step Content */}
        <View style={{ width: '100%', flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start', minHeight: 0 }}>
          {currentStep === 'totalAssets' && (
            <>
              <CustomText style={{ fontSize: 22, fontWeight: '700', marginBottom: spacing.lg, fontFamily: typography.fontFamily.body, textAlign: 'left' }}>
                What are your approximate total assets?
              </CustomText>
              <TextInput
                value={formatCurrency(formData.totalAssets)}
                onChangeText={(text: string) => {
                  const numeric = text.replace(/[^\d]/g, '');
                  handleChange('totalAssets', numeric);
                }}
                placeholder="$0"
                keyboardType="numeric"
                style={{
                  height: 40,
                  borderWidth: 1,
                  borderColor: colors.gray[200],
                  borderRadius: 6,
                  paddingHorizontal: 12,
                  marginBottom: 16,
                  outlineStyle: 'none',
                  width: 400,
                  fontFamily: typography.fontFamily.body,
                  fontSize: 16,
                  backgroundColor: colors.white,
                }}
              />
            </>
          )}
          {currentStep === 'annualSalary' && (
            <>
              <CustomText style={{ fontSize: 22, fontWeight: '700', marginBottom: spacing.lg, fontFamily: typography.fontFamily.body, textAlign: 'left' }}>
                What is your approximate annual salary?
              </CustomText>
              <TextInput
                value={formatCurrency(formData.annualSalary)}
                onChangeText={(text: string) => {
                  const numeric = text.replace(/[^\d]/g, '');
                  handleChange('annualSalary', numeric);
                }}
                placeholder="$0"
                keyboardType="numeric"
                style={{
                  height: 40,
                  borderWidth: 1,
                  borderColor: colors.gray[200],
                  borderRadius: 6,
                  paddingHorizontal: 12,
                  marginBottom: 16,
                  outlineStyle: 'none',
                  width: 400,
                  fontFamily: typography.fontFamily.body,
                  fontSize: 16,
                  backgroundColor: colors.white,
                }}
              />
            </>
          )}
          {currentStep === 'monthlyExpenses' && (
            <>
              <CustomText style={{ fontSize: 22, fontWeight: '700', marginBottom: spacing.lg, fontFamily: typography.fontFamily.body, textAlign: 'left' }}>
                What are your approximate average monthly expenses?
              </CustomText>
              <TextInput
                value={formatCurrency(formData.monthlyExpenses)}
                onChangeText={(text: string) => {
                  const numeric = text.replace(/[^\d]/g, '');
                  handleChange('monthlyExpenses', numeric);
                }}
                placeholder="$0"
                keyboardType="numeric"
                style={{
                  height: 40,
                  borderWidth: 1,
                  borderColor: colors.gray[200],
                  borderRadius: 6,
                  paddingHorizontal: 12,
                  marginBottom: 16,
                  outlineStyle: 'none',
                  width: 400,
                  fontFamily: typography.fontFamily.body,
                  fontSize: 16,
                  backgroundColor: colors.white,
                }}
              />
            </>
          )}
          {currentStep === 'confidence' && (
            <>
              <CustomText style={{ fontSize: 22, fontWeight: '700', marginBottom: spacing.lg, fontFamily: typography.fontFamily.body, textAlign: 'left' }}>
                How confident do you feel in your current financial plan?
              </CustomText>
              <View style={{ flexDirection: 'row', gap: 0, width: '100%', justifyContent: 'space-between', marginBottom: spacing.lg }}>
                {[...Array(10)].map((_, i) => {
                  const value = i + 1;
                  const selected = formData.confidenceLevel === value;
                  return (
                    <Pressable
                      key={value}
                      onPress={() => handleChange('confidenceLevel', value)}
                      style={{
                        flex: 1,
                        maxWidth: 56,
                        minWidth: 36,
                        height: 56,
                        borderRadius: 28,
                        backgroundColor: selected ? colors.primary : colors.white,
                        borderWidth: 2,
                        borderColor: selected ? colors.primary : colors.gray[300],
                        justifyContent: 'center',
                        alignItems: 'center',
                        boxShadow: selected ? '0 2px 8px rgba(30, 136, 229, 0.2)' : 'none',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        outline: 'none',
                        margin: '0 2px',
                      }}
                      onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => (e.currentTarget.style.boxShadow = '0 2px 8px rgba(30, 136, 229, 0.15)')}
                      onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => (e.currentTarget.style.boxShadow = selected ? '0 2px 8px rgba(30, 136, 229, 0.2)' : 'none')}
                    >
                      <CustomText style={{
                        color: selected ? colors.white : colors.gray[700],
                        fontWeight: '600',
                        fontSize: 20,
                      }}>
                        {value}
                      </CustomText>
                    </Pressable>
                  );
                })}
              </View>
              {/* Descriptions under the scale */}
              <View style={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: spacing.md }}>
                <CustomText style={{ fontSize: 14, color: colors.gray[500], minWidth: 80, textAlign: 'left' }}>Not Confident</CustomText>
                <CustomText style={{ fontSize: 14, color: colors.gray[500], minWidth: 120, textAlign: 'center' }}>Somewhat Confident</CustomText>
                <CustomText style={{ fontSize: 14, color: colors.gray[500], minWidth: 120, textAlign: 'right' }}>Extremely Confident</CustomText>
              </View>
            </>
          )}
          {currentStep === 'contact' && (
            <>
              <CustomText style={{ fontSize: 22, fontWeight: '700', marginBottom: spacing.lg, fontFamily: typography.fontFamily.body, textAlign: 'left' }}>
                How should we contact you?
              </CustomText>
              <View style={{ width: 800, gap: 0 }}>
                <View style={{ display: 'flex', flexDirection: 'row', gap: spacing.md, marginBottom: 0 }}>
                  <View style={{ flex: 1 }}>
                    <CustomText style={{ marginBottom: 8, color: colors.gray[700] }}>Full Name</CustomText>
                    <TextInput
                      value={formData.name}
                      onChangeText={(text: string) => handleChange('name', text)}
                      placeholder="Enter your full name"
                      placeholderTextColor={colors.gray[500]}
                      style={{
                        height: 40,
                        borderWidth: 1,
                        borderColor: colors.gray[200],
                        borderRadius: 6,
                        paddingHorizontal: 12,
                        outlineStyle: 'none',
                        width: '100%',
                        fontFamily: typography.fontFamily.body,
                        fontSize: 14,
                        backgroundColor: colors.white,
                      }}
                    />
                  </View>
                  <View style={{ flex: 1 }}>
                    <CustomText style={{ marginBottom: 8, color: colors.gray[700] }}>Phone Number</CustomText>
                    <TextInput
                      value={formatPhoneNumber(formData.phone)}
                      onChangeText={(text: string) => {
                        // Only allow up to 10 digits
                        const numeric = text.replace(/[^\d]/g, '').slice(0, 10);
                        handleChange('phone', numeric);
                      }}
                      placeholder="978-844-3585"
                      placeholderTextColor={colors.gray[500]}
                      keyboardType="phone-pad"
                      style={{
                        height: 40,
                        borderWidth: 1,
                        borderColor: colors.gray[200],
                        borderRadius: 6,
                        paddingHorizontal: 12,
                        outlineStyle: 'none',
                        width: '100%',
                        fontFamily: typography.fontFamily.body,
                        fontSize: 14,
                        backgroundColor: colors.white,
                      }}
                    />
                  </View>
                  <View style={{ flex: 1 }}>
                    <CustomText style={{ marginBottom: 8, color: colors.gray[700] }}>Email Address</CustomText>
                    <TextInput
                      value={formData.email}
                      onChangeText={(text: string) => handleChange('email', text)}
                      placeholder="you@example.com"
                      placeholderTextColor={colors.gray[500]}
                      keyboardType="email-address"
                      style={{
                        height: 40,
                        borderWidth: 1,
                        borderColor: colors.gray[200],
                        borderRadius: 6,
                        paddingHorizontal: 12,
                        outlineStyle: 'none',
                        width: '100%',
                        fontFamily: typography.fontFamily.body,
                        fontSize: 14,
                        backgroundColor: colors.white,
                      }}
                    />
                  </View>
                </View>
                <View style={{ marginTop: spacing.lg }}>
                  <CustomText style={{ marginBottom: 8, color: colors.gray[700] }}>Preferred Contact Method</CustomText>
                  <View style={{ display: 'flex', flexDirection: 'row', gap: spacing.md }}>
                    <Pressable
                      onPress={() => handleChange('preferredContact', 'email')}
                      style={{
                        flex: 1,
                        height: 40,
                        borderWidth: 1,
                        borderColor: formData.preferredContact === 'email' ? colors.primary : colors.gray[200],
                        borderRadius: 6,
                        backgroundColor: formData.preferredContact === 'email' ? colors.primary : colors.white,
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <CustomText style={{ 
                        color: formData.preferredContact === 'email' ? colors.white : colors.gray[700], 
                        fontFamily: typography.fontFamily.body, 
                        fontSize: 14 
                      }}>
                        Email
                      </CustomText>
                    </Pressable>
                    <Pressable
                      onPress={() => handleChange('preferredContact', 'phone')}
                      style={{
                        flex: 1,
                        height: 40,
                        borderWidth: 1,
                        borderColor: formData.preferredContact === 'phone' ? colors.primary : colors.gray[200],
                        borderRadius: 6,
                        backgroundColor: formData.preferredContact === 'phone' ? colors.primary : colors.white,
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <CustomText style={{ 
                        color: formData.preferredContact === 'phone' ? colors.white : colors.gray[700], 
                        fontFamily: typography.fontFamily.body, 
                        fontSize: 14 
                      }}>
                        Phone
                      </CustomText>
                    </Pressable>
                  </View>
                </View>
              </View>
            </>
          )}
          {currentStep === 'thoughts' && (
            <>
              <CustomText style={{ fontSize: 22, fontWeight: '700', marginBottom: spacing.lg, fontFamily: typography.fontFamily.body, textAlign: 'left' }}>
                Please share any impressions or thoughts from your personality test
              </CustomText>
              <TextInput
                value={formData.thoughts}
                onChangeText={(text: string) => handleChange('thoughts', text)}
                placeholder="Share your thoughts..."
                placeholderTextColor={colors.gray[500]}
                multiline
                numberOfLines={4}
                style={{
                  height: 120,
                  borderWidth: 1,
                  borderColor: colors.gray[200],
                  borderRadius: 6,
                  paddingHorizontal: 12,
                  paddingTop: 12,
                  outlineStyle: 'none',
                  width: '100%',
                  fontFamily: typography.fontFamily.body,
                  fontSize: 16,
                  backgroundColor: colors.white,
                  resize: 'none',
                }}
              />
            </>
          )}
          {currentStep === 'confirmation' && (
            <>
              <CustomText style={{ fontSize: 22, fontWeight: '700', marginBottom: spacing.lg, fontFamily: typography.fontFamily.body, textAlign: 'left' }}>
                Please confirm your information:
              </CustomText>
              <View style={{ marginBottom: spacing.md, fontSize: 18, width: '100%' }}>
                {/* Contact info in a row - now first */}
                <View style={{ display: 'flex', flexDirection: 'row', gap: spacing.lg, marginBottom: spacing.lg, width: '100%', justifyContent: 'space-between' }}>
                  <CustomText><b>Name:</b> {formData.name}</CustomText>
                  <CustomText><b>Phone:</b> {formatPhoneNumber(formData.phone)}</CustomText>
                  <CustomText><b>Email:</b> {formData.email}</CustomText>
                </View>
                {/* Assets, Salary, Expenses in a row - now second */}
                <View style={{ display: 'flex', flexDirection: 'row', gap: spacing.lg, marginBottom: spacing.lg, width: '100%', justifyContent: 'space-between' }}>
                  <CustomText><b>Total Assets:</b> ${formData.totalAssets ? Number(formData.totalAssets).toLocaleString() : ''}</CustomText>
                  <CustomText><b>Annual Salary:</b> ${formData.annualSalary ? Number(formData.annualSalary).toLocaleString() : ''}</CustomText>
                  <CustomText><b>Monthly Expenses:</b> ${formData.monthlyExpenses ? Number(formData.monthlyExpenses).toLocaleString() : ''}</CustomText>
                </View>
                {/* Confidence as x/10 - now third */}
                <CustomText style={{ marginBottom: spacing.lg }}><b>Confidence in Plan:</b> {formData.confidenceLevel}/10</CustomText>
                <CustomText style={{ marginBottom: spacing.lg }}>
                  <b>Preferred Contact:</b> {formData.preferredContact.charAt(0).toUpperCase() + formData.preferredContact.slice(1)}
                </CustomText>
                <CustomText style={{ marginBottom: spacing.sm }}>
                  <b>Thoughts:</b> {formData.thoughts}
                </CustomText>
              </View>
              {/* Confirmation checkbox moved here */}
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.lg }}>
                <Pressable
                  onPress={() => handleChange('wantsContact', !formData.wantsContact)}
                  style={{
                    width: 20,
                    height: 20,
                    borderWidth: 1,
                    borderColor: colors.gray[300],
                    borderRadius: 4,
                    backgroundColor: formData.wantsContact ? colors.primary : colors.white,
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginRight: 8,
                    cursor: 'pointer',
                  }}
                >
                  {formData.wantsContact && (
                    // SVG check mark
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 7.5L6 10.5L11 4.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </Pressable>
                <CustomText style={{ fontSize: 16, color: colors.gray[700], fontFamily: typography.fontFamily.body }}>
                  I would like to be contacted by a financial professional
                </CustomText>
              </View>
            </>
          )}
        </View>
        {/* Navigation Buttons at the bottom, always visible */}
        <View style={{ width: '100%', display: 'flex', flexDirection: 'row', gap: spacing.md, marginTop: spacing.xl, flexShrink: 0, background: colors.white }}>
          {stepIndex > 0 && (
            <Button label="Back" variant="secondary" onPress={handleBack} style={{ minWidth: 0, width: stepIndex < steps.length - 1 ? '50%' : '50%' }} />
          )}
          {stepIndex < steps.length - 1 && (
            <Button 
              label="Continue" 
              onPress={handleNext} 
              style={{ 
                minWidth: 0, 
                width: stepIndex > 0 ? '50%' : '100%', 
                backgroundColor: colors.primary, 
                borderColor: colors.primary 
              }} 
              textStyle={{ color: colors.white }}
            />
          )}
          {stepIndex === steps.length - 1 && (
            <Button 
              label="Submit" 
              onPress={handleSubmit} 
              style={{ 
                minWidth: 0, 
                width: stepIndex > 0 ? '50%' : '100%', 
                backgroundColor: colors.primary, 
                borderColor: colors.primary 
              }} 
              textStyle={{ color: colors.white }}
              disabled={!formData.wantsContact} 
            />
          )}
        </View>
      </View>
    </Modal>
  );
} 